import { type InfiniteData, useQueryClient } from "@tanstack/react-query";
import { ChevronDown } from "@untitled-ui/icons-react";
import { isSameDay } from "date-fns";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroller";

import Loading from "@/components/icons/Loading";
import LoadingTyping from "@/components/loadings/LoadingTyping";
import UserAvatar from "@/components/media/UserAvatar";
import Badge from "@/components/text/Badge";
import { cn } from "@/lib/utils";
import { useFetchMe } from "@/user/services";
import { formatDate } from "@/utils";

import {
  fetchConversationMessages,
  useFetchConversationMessages,
} from "../../services";
import {
  type ConversationList,
  conversations,
} from "../../services/conversation";
import { useVoicePlaybackContext } from "../../store/voicePlayback";
import type { Conversation, Message, WhisperUser } from "../../types";
import { scrollToMessageElement } from "../../utils/message";
import MessageCard from "./MessageCard";

interface ConversationContainerProps {
  typingUsers: WhisperUser[];
  conversation: Conversation<true, true, true>;
  onReply?: (message: Message<true>) => void;
  className?: string;
  messageClassName?: string;
}

const SCROLL_END_THRESHOLD = 10;

export default function ConversationContainer({
  typingUsers,
  conversation,
  onReply,
  className,
  messageClassName,
}: ConversationContainerProps) {
  const { t } = useTranslation("messenger.user");
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useFetchConversationMessages(conversation.id);

  const messages = useMemo(
    () => data?.pages.flatMap((page) => page.data) || [],
    [data?.pages],
  );
  const [focusedMessages, setFocusedMessages] = useState<Message<true>[]>();
  const visibleMessages = focusedMessages || messages;

  const { data: currentUser } = useFetchMe();
  const queryClient = useQueryClient();
  const setScrollToMessage = useVoicePlaybackContext(
    (state) => state.setScrollToMessage,
  );
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);

  useEffect(() => {
    if (!conversation.id) return;

    queryClient.setQueriesData<InfiniteData<ConversationList>>(
      { queryKey: conversations.allInfinite._def },
      (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.map((conv) =>
              conv.id === conversation.id
                ? { ...conv, unread_messages_count: 0 }
                : conv,
            ),
          })),
        };
      },
    );

    queryClient.invalidateQueries(conversations.unreadCount);
  }, [conversation.id, queryClient]);
  const pendingScrollMessageId = useRef<number | null>(null);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);

  const prevInfoRef = useRef<{
    firstId: number | null;
    lastId: number | null;
    height: number;
  }>({
    firstId: null,
    lastId: null,
    height: 0,
  });

  useEffect(() => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const prev = prevInfoRef.current;

    const newFirstId = visibleMessages[0]?.id ?? null;
    const newLastId = visibleMessages[visibleMessages.length - 1]?.id ?? null;

    if (prev.firstId !== null && newFirstId && newFirstId !== prev.firstId) {
      const newMsg = visibleMessages[0];
      const isMe = currentUser?.id === newMsg?.user?.id;

      if (isMe) {
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
        });
        setHasNewMessages(false);
        setNewMessageCount(0);
      } else {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const atBottom =
          scrollTop + clientHeight >= scrollHeight - SCROLL_END_THRESHOLD;

        if (!atBottom) {
          setHasNewMessages(true);
          setNewMessageCount((c) => c + 1);
        } else {
          setHasNewMessages(false);
          setNewMessageCount(0);
        }
      }
    } else if (prev.lastId !== null && newLastId && newLastId !== prev.lastId) {
      const addedHeight = container.scrollHeight - prev.height;
      container.scrollTop = container.scrollTop + addedHeight;
    } else {
      container.scrollTop = container.scrollHeight;
    }

    prevInfoRef.current = {
      firstId: newFirstId,
      lastId: newLastId,
      height: container.scrollHeight,
    };
  }, [currentUser?.id, visibleMessages]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      stickToBottomRef.current =
        scrollTop + clientHeight >= scrollHeight - SCROLL_END_THRESHOLD;

      if (stickToBottomRef.current) {
        setHasNewMessages(false);
        setNewMessageCount(0);
      }
    };

    const observer = new ResizeObserver(() => {
      if (!stickToBottomRef.current) return;
      container.scrollTop = container.scrollHeight;
    });
    observer.observe(container);

    container.addEventListener("scroll", onScroll);
    return () => {
      container.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!pendingScrollMessageId.current) return;
    if (
      !visibleMessages.some(
        (message) => message.id === pendingScrollMessageId.current,
      )
    ) {
      return;
    }

    scrollToMessageElement(pendingScrollMessageId.current);
    pendingScrollMessageId.current = null;
  }, [visibleMessages]);

  const handleChevronClick = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    setHasNewMessages(false);
    setNewMessageCount(0);
  };

  const scrollToMessage = useCallback(
    async (messageId: number) => {
      if (scrollToMessageElement(messageId)) return;

      const [fromPage, toPage] = await Promise.all([
        fetchConversationMessages(conversation.id, {
          filters: { from_message_id: messageId },
        }),
        fetchConversationMessages(conversation.id, {
          filters: { to_message_id: messageId },
        }),
      ]);

      const byId = new Map<number, Message<true>>();
      for (const message of [...fromPage.data, ...toPage.data]) {
        byId.set(message.id, message);
      }

      pendingScrollMessageId.current = messageId;
      setFocusedMessages([...byId.values()]);
    },
    [conversation.id],
  );

  useEffect(() => {
    setScrollToMessage(scrollToMessage);
    return () => setScrollToMessage(null);
  }, [scrollToMessage, setScrollToMessage]);

  const renderMessage = (message: Message<true>, index: number) => {
    if (!currentUser || !message.user) return null;

    const isMe = currentUser.id === message.user.id;
    const next = visibleMessages[index + 1];
    const prev = visibleMessages[index - 1];

    const isGrouped =
      next?.user?.id === message.user.id &&
      isSameDay(new Date(message.created_at), new Date(next.created_at));

    const isGroupedBelow =
      !!prev?.user?.id &&
      prev.user.id === message.user.id &&
      isSameDay(new Date(message.created_at), new Date(prev.created_at));

    const showAvatar =
      !isMe &&
      !(
        prev?.user?.id === message.user.id &&
        isSameDay(new Date(message.created_at), new Date(prev.created_at))
      );

    const showSenderName =
      !isMe &&
      !(
        next?.user?.id === message.user.id &&
        isSameDay(new Date(message.created_at), new Date(next.created_at))
      );

    const isNewDay =
      !next ||
      !isSameDay(new Date(message.created_at), new Date(next.created_at));

    const isGroup = conversation.type === "group";
    const isRead = conversation.last_outgoing_read_message_id
      ? message.id <= conversation.last_outgoing_read_message_id
      : false;
    return (
      <Fragment key={message.id}>
        <MessageCard
          isMe={isMe}
          message={message}
          showAvatar={showAvatar}
          showSenderName={showSenderName}
          isGrouped={isGrouped}
          isGroupedBelow={isGroupedBelow}
          isRead={isRead}
          isGroup={isGroup}
          className={messageClassName}
          onReply={onReply}
          onReplyPreviewClick={scrollToMessage}
        />
        {isNewDay && (
          <div className="flex w-full items-center justify-center py-4">
            <div className="flex w-full items-center gap-3">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="rounded-full px-4 py-1 text-sm font-medium whitespace-nowrap text-gray-600">
                {(() => {
                  const messageDate = new Date(message.created_at);
                  const today = new Date();
                  const isToday =
                    messageDate.getFullYear() === today.getFullYear() &&
                    messageDate.getMonth() === today.getMonth() &&
                    messageDate.getDate() === today.getDate();

                  return isToday
                    ? t("Today", { ns: "core.shared" })
                    : formatDate(message.created_at);
                })()}
              </span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
          </div>
        )}
      </Fragment>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto" ref={scrollRef}>
      {isLoading && (
        <div className="flex h-full items-center justify-center">
          <Loading className="size-8" />
        </div>
      )}
      <InfiniteScroll
        pageStart={0}
        loadMore={() => !isFetchingNextPage && fetchNextPage()}
        hasMore={!focusedMessages && hasNextPage && !isFetchingNextPage}
        useWindow={false}
        isReverse
        initialLoad={false}
        threshold={10}
        className={cn("flex flex-col-reverse items-start", className ?? "p-6")}
        getScrollParent={() => scrollRef.current}
      >
        {typingUsers.length > 0 && (
          <div className="mt-4 flex items-center gap-2">
            <div className="flex gap-3 self-end">
              <UserAvatar
                user={{
                  id: typingUsers[0].id,
                  first_name: typingUsers[0].first_name,
                  last_name: typingUsers[0].last_name,
                  profile_picture_thumb: typingUsers[0].profile_picture_thumb,
                }}
                size="md"
                disableTooltip
              />
              <div className="flex flex-1 flex-col items-start gap-1.5">
                <div className="flex items-center justify-between">
                  <div className="text-gray-700">
                    {typingUsers[0].first_name + " " + typingUsers[0].last_name}
                    {typingUsers.length > 1 && (
                      <span className="text-xs text-gray-400">
                        {" "}
                        {t("and {{count}} other people", {
                          count: typingUsers.length - 1,
                        })}
                      </span>
                    )}
                  </div>
                </div>
                <div className="rounded-lg rounded-tr-none border bg-gray-50 px-3.5 py-2.5">
                  <LoadingTyping />
                </div>
              </div>
            </div>
          </div>
        )}
        {visibleMessages.length === 0 ? (
          <div />
        ) : (
          visibleMessages.map(renderMessage)
        )}
        {!focusedMessages && hasNextPage && <Loading />}
      </InfiniteScroll>

      {hasNewMessages && (
        <div
          onClick={handleChevronClick}
          className="fixed end-15 bottom-30 cursor-pointer rounded-full border border-gray-200 bg-white p-2"
        >
          <div className="flex flex-col items-center gap-2">
            <ChevronDown className="text-primary-600 size-5" />
            <Badge size="sm">{newMessageCount}</Badge>
          </div>
        </div>
      )}
    </div>
  );
}
